// Skills-coaching library for the source-examination interaction type.
//
// Ported verbatim from the reference revision app (revise-reference/NSC2025-main/
// app.js → SKILL_DATA). This is the "built-in exam coach": for each of the 7
// skill types a question can carry, a method guide (intro → numbered steps →
// worked example → common mistakes) the learner can open per question.
//
// Static, not authored per-question — the coaching is consistent exam technique,
// keyed off SourceExaminationQuestion.skill. See REVISION_TEMPLATE_PLAN.md §2.4.

import type { SourceExamSkill } from '../../slideTypes';

export interface SkillCoaching {
  title: string;
  sub: string;
  /** May contain inline <strong>/<em> markup, like the reference. */
  intro: string;
  /** [boldLead, rest] per step. */
  steps: Array<[string, string]>;
  example: string;
  warn: string;
  /** Short call-to-action shown on the coach button for this skill. */
  buttonText: string;
}

export const SOURCE_EXAM_COACHING: Record<SourceExamSkill, SkillCoaching> = {
  extraction: {
    title: 'Extraction',
    sub: 'Finding information in sources',
    intro:
      'Extraction questions ask you to <strong>find specific information that is directly stated in the source</strong>. The answer is IN the text, with no interpretation needed.',
    steps: [
      ['Read the question first, then the source.', 'Underline the KEY WORDS. What exactly are you being asked to find? A person? A reason? A city? A date?'],
      ['Scan the source for those key words', 'or their synonyms. The answer is usually within one or two sentences of where the key words appear.'],
      ['Check your answer is actually IN the source.', "If you can't point to the exact sentence, you're probably interpreting rather than extracting."],
      ['Count carefully.', 'If the question says "identify TWO reasons," give exactly two. Missing one loses marks; adding extras wastes time.'],
    ],
    example: '<strong>Q:</strong> "Who replaced Byrnes as Secretary of State?" → Scan for "Byrnes" → "George Marshall" = 1 mark.',
    warn: "Using your own knowledge instead of the source. Giving information that's true but not in THIS source. Wrong count of points.",
    buttonText: 'Not sure how to find the answer? Tap here for extraction tips',
  },
  concept: {
    title: 'Concept Definition',
    sub: 'Explaining terms in your own words',
    intro:
      '"Define the concept… in your own words" tests whether you understand a term well enough to explain it <strong>without copying the source</strong>.',
    steps: [
      ['Identify what KIND of thing it is.', 'Policy? Metaphor? Ideology? Process? Place it in a category first: "The Iron Curtain was a metaphor…"'],
      ['Explain what it means, not just what it is.', 'Say what it involved, who it affected, why it mattered. A definition that could apply to anything is too vague.'],
      ['Connect to the historical context', 'when the question says "in the context of." Link to the specific period, event, or situation.'],
    ],
    example:
      '<strong>Iron Curtain:</strong> A metaphor (category) for the dividing line separating communist Eastern Europe from capitalist Western Europe (meaning), after the USSR extended its sphere of influence post-1945 (context).',
    warn: 'Repeating the source word-for-word. Vague definitions without context. Confusing similar concepts (Iron Curtain vs Berlin Wall).',
    buttonText: 'Need help defining concepts in your own words? Tap for guidance',
  },
  interpretation: {
    title: 'Interpretation',
    sub: 'Explaining what evidence means and why it matters',
    intro:
      'Interpretation questions ask you to go <strong>beyond what the source says</strong> to explain what it <strong>means</strong>, what it <strong>implies</strong>, or <strong>why it matters</strong>.',
    steps: [
      ['Start with what the source SAYS, then explain what it MEANS.', 'Use bridge phrases: "This suggests…" / "This implies…" / "The significance is…"'],
      ['Ask WHY, not just WHAT.', 'Why did it happen? Why does it matter? What were the consequences?'],
      ['Consider language and purpose.', '"Armageddon" is much stronger than "important meeting". What does that word choice reveal?'],
      ['For "comment on" questions:', 'Interpret + explain significance. Connect to the bigger historical picture.'],
    ],
    example:
      '<strong>Weak:</strong> "Acheson called it \'Armageddon.\'" (just repeats source). <strong>Strong:</strong> Acheson used apocalyptic language because he saw the decision as existential, revealing the Cold War mentality where every decision was framed as good vs evil.',
    warn: 'Repeating the source without explaining meaning. If every sentence starts "The source says…" you\'re describing, not interpreting.',
    buttonText: 'Struggling to explain WHY, not just WHAT? Tap for interpretation tips',
  },
  analysis: {
    title: 'Source + Own Knowledge',
    sub: "Combining evidence with what you've studied",
    intro:
      'These questions require <strong>two things</strong>: evidence from the source AND additional information from your own studies. You need both.',
    steps: [
      ['Identify what the SOURCE tells you.', 'What evidence or clues does it provide? Start your answer by referring to the source.'],
      ['Add what you KNOW from your studies.', 'Additional facts, context, or background that helps explain what the source shows.'],
      ['LINK them together.', 'Show how your knowledge EXPLAINS what the source shows, or how the source CONFIRMS what you\'ve studied.'],
    ],
    example:
      '<strong>Source:</strong> Cartoon shows Greece/Turkey outside the Soviet bloc. <strong>Own K:</strong> The Truman Doctrine (1947) provided US aid to these countries. <strong>Link:</strong> The source shows the outcome; my knowledge explains the cause.',
    warn: 'Using only the source (no own knowledge). Using only own knowledge (no source). Listing both without connecting them.',
    buttonText: 'Not sure how to combine source + own knowledge? Tap for help',
  },
  reliability: {
    title: 'Reliability & Bias',
    sub: 'Evaluating whether you can trust a source',
    intro:
      'Reliability questions ask: <strong>can a historian trust this source?</strong> The answer is almost never simply "yes" or "no." Weigh strengths against limitations.',
    steps: [
      ['WHO created it?', 'First-hand knowledge? Insider access? Eyewitness? Politician with an agenda?'],
      ['WHEN was it created?', 'During the events (immediate but less reflective) or after (more perspective, but shaped by hindsight)?'],
      ['WHY was it created?', 'To inform? Persuade? Justify? Rally support? Purpose shapes content.'],
      ['WHAT perspective does it represent?', "Every source has a perspective. That doesn't make it unreliable, but a historian needs to be aware of it."],
    ],
    example:
      "<strong>Churchill's Iron Curtain speech:</strong> Reliable because he had first-hand experience at Yalta/Potsdam (WHO) and the speech was contemporaneous (WHEN). However, his anti-communist stance and goal of rallying US support (WHY) mean the language may overstate the threat.",
    warn: '"Reliable because it\'s a primary source": primary sources can be biased. "Unreliable because biased": all sources have a perspective. Always weigh both sides.',
    buttonText: 'Not sure how to evaluate reliability? Tap for a step-by-step guide',
  },
  comparison: {
    title: 'Source Comparison',
    sub: 'Finding connections between two sources',
    intro:
      'Comparison questions ask you to find <strong>connections between sources</strong>. How does one support, contradict, or extend another? Discuss BOTH together, not separately.',
    steps: [
      ['Identify the THEME that connects them.', 'What topic or idea do both sources address? This is your bridge.'],
      ['Find specific EVIDENCE in each source', 'that relates to this shared theme. Concrete details from both, not vague summaries.'],
      ['Explain the CONNECTION.', 'Use linking phrases: "Source A states… and this is supported by Source B which shows…" / "Together, these sources demonstrate…"'],
      ['For 4-mark questions:', 'TWO comparison points, each with evidence from both sources and an explanation of the connection. 2×2 = 4 marks.'],
    ],
    example:
      '<strong>Theme:</strong> Both address Soviet expansion. <strong>1A:</strong> Churchill warns of Iron Curtain from Stettin to Trieste. <strong>1C:</strong> Acheson shows the threat materialising in Greece/Turkey. <strong>Link:</strong> 1C confirms and escalates the warning Churchill made in 1A.',
    warn: 'Describing each source separately without connections. Saying "the sources are similar" without explaining HOW. Comparing authors instead of content.',
    buttonText: 'Need help comparing sources? Tap for comparison tips',
  },
  synthesis: {
    title: 'Paragraph Writing',
    sub: 'Synthesising sources + own knowledge into an argument',
    intro:
      "The paragraph question tests <strong>synthesis</strong>: weaving source evidence and your own knowledge into a coherent argument. It's assessed holistically.",
    steps: [
      ['TOPIC SENTENCE:', "Address the question directly in your first sentence. State your argument. Don't start with background; start with your answer."],
      ['SOURCE EVIDENCE:', 'Use specific evidence from at least TWO sources. Reference them by number (Source 1A, Source 1C).'],
      ['OWN KNOWLEDGE:', 'Add at least ONE piece of information not in the sources. It must support your argument.'],
      ['CONCLUDING SENTENCE:', 'Return to the question. Name the mechanism: HOW and WHY, not just WHAT.'],
    ],
    example:
      '<strong>L3 (6–8):</strong> Clear topic sentence → specific source evidence woven in → own knowledge that extends the argument → conclusion that synthesises (shows HOW and WHY).',
    warn: 'A list of disconnected facts with no topic sentence. Naming sources without using their evidence. Missing own knowledge.',
    buttonText: 'Need help building your paragraph? Tap for the step-by-step method',
  },
};

/** Display labels for the skill chip on each question. */
export const SKILL_LABELS: Record<SourceExamSkill, string> = {
  extraction: 'Extraction',
  concept: 'Concept',
  interpretation: 'Interpretation',
  analysis: 'Analysis',
  reliability: 'Reliability',
  comparison: 'Comparison',
  synthesis: 'Synthesis',
};

/** Short label for each cognitive level (results bars + chips). */
export const COGNITIVE_LEVEL_LABELS: Record<'L1_recall' | 'L2_interpret' | 'L3_apply', string> = {
  L1_recall: 'L1',
  L2_interpret: 'L2',
  L3_apply: 'L3',
};
